import { BreadCrumbTrailEntryViewExtended } from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class SearchConfigResource<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * No description
	 *
	 * @tags Search-Config-Resource
	 * @name GetConfigurationData
	 * @summary Get the configuration node data.
	 * @request GET:/api/v2/configuration
	 * @response `200` `BreadCrumbTrailEntryViewExtended` The request is completed successfully.
	 * @response `400` `BreadCrumbTrailEntryViewExtended` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `500` `BreadCrumbTrailEntryViewExtended` Internal server error. Additional details will be contained on the server logs.
	 */
	getConfigurationData = (
		query: {
			nodeName: string;
			/** Locale name */
			locale?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['getConfigurationData'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/configuration`,
				params: _params,
				query: query,
				methodName: 'getConfigurationData',
			};
			logger.trace(traceData);
		}
		return this.http.request<BreadCrumbTrailEntryViewExtended, BreadCrumbTrailEntryViewExtended>({
			path: `/api/v2/configuration`,
			method: 'GET',
			query: query,
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Config-Resource
	 * @name AddConfiguration
	 * @summary Create the configuration node in ZooKeeper.
	 * @request POST:/api/v2/configuration
	 * @response `201` `Record<string,string>` The requested resource has been created.
	 * @response `400` `Record<string,string>` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `500` `Record<string,string>` Internal server error. Additional details will be contained on the server logs.
	 */
	addConfiguration = (
		query: {
			nodeName: string;
			/** Locale name */
			locale?: string;
		},
		data: string,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['addConfiguration'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/configuration`,
				params: _params,
				query: query,
				body: data,
				methodName: 'addConfiguration',
			};
			logger.trace(traceData);
		}
		return this.http.request<Record<string, string>, Record<string, string>>({
			path: `/api/v2/configuration`,
			method: 'POST',
			query: query,
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Config-Resource
	 * @name UpdateColorConfiguration
	 * @summary Update the configuration node in ZooKeeper.
	 * @request PATCH:/api/v2/configuration
	 * @response `201` `Record<string,string>` The requested resource has been created.
	 * @response `400` `Record<string,string>` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `500` `Record<string,string>` Internal server error. Additional details will be contained on the server logs.
	 */
	updateColorConfiguration = (
		query: {
			nodeName: string;
			/** Locale name */
			locale?: string;
		},
		data: string,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['updateColorConfiguration'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/configuration`,
				params: _params,
				query: query,
				body: data,
				methodName: 'updateColorConfiguration',
			};
			logger.trace(traceData);
		}
		return this.http.request<Record<string, string>, Record<string, string>>({
			path: `/api/v2/configuration`,
			method: 'PATCH',
			query: query,
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
